import React, { useState, useEffect, useCallback } from 'react';

// Calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Calculate payment based on distance
function calculatePayment(distance) {
  if (distance < 2) return 2.50;
  if (distance >= 2 && distance <= 4) return 2.80;
  return 3.00;
}

function AvailableDeliveries({ driverLocation, onPickup, maxPackages }) {
  const [deliveries, setDeliveries] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'nearby', 'high-pay'

  const generateMockDeliveries = useCallback(() => {
    const warehouses = [
      { name: 'Milan Center Warehouse', lat: 45.4642, lng: 9.1900 },
      { name: 'Milan North Hub', lat: 45.5000, lng: 9.1800 },
      { name: 'Milan South Hub', lat: 45.4400, lng: 9.1950 }
    ];

    const mockDeliveries = [];
    const numDeliveries = Math.floor(Math.random() * 8) + 5; // 5-12 deliveries

    for (let i = 0; i < numDeliveries; i++) {
      const warehouse = warehouses[Math.floor(Math.random() * warehouses.length)];
      
      // Random delivery location within 5km
      const distance = Math.random() * 5 + 0.5; // 0.5 to 5.5 km
      const angle = Math.random() * 2 * Math.PI;
      const deliveryLat = warehouse.lat + (distance / 111) * Math.cos(angle);
      const deliveryLng = warehouse.lng + (distance / (111 * Math.cos(warehouse.lat * Math.PI / 180))) * Math.sin(angle);

      const distanceFromDriver = calculateDistance(
        driverLocation.lat, 
        driverLocation.lng, 
        deliveryLat, 
        deliveryLng
      );

      const payment = calculatePayment(distanceFromDriver);

      mockDeliveries.push({
        id: 'del_' + Date.now() + '_' + i,
        orderId: 'ORD' + (10000 + i),
        warehouse: warehouse.name,
        warehouseLocation: { lat: warehouse.lat, lng: warehouse.lng },
        deliveryAddress: `Via ${['Roma', 'Milano', 'Dante', 'Verdi', 'Garibaldi'][Math.floor(Math.random() * 5)]} ${Math.floor(Math.random() * 100) + 1}, Milano`,
        deliveryLocation: { lat: deliveryLat, lng: deliveryLng },
        distance: parseFloat(distanceFromDriver.toFixed(2)),
        payment: payment,
        customerName: `Customer ${i + 1}`,
        customerPhone: '+39 3' + Math.floor(Math.random() * 900000000 + 100000000),
        packageCode: 'PKG' + (1000 + i),
        deliveryCode: String(Math.floor(Math.random() * 900000) + 100000), // 6-digit code
        estimatedTime: Math.ceil(distanceFromDriver / 25 * 60), // minutes at 25km/h avg
        priority: Math.random() > 0.7 ? 'high' : 'normal',
        createdAt: new Date(Date.now() - Math.random() * 3600000).toISOString() // Within last hour
      });
    }

    // Sort by distance
    mockDeliveries.sort((a, b) => a.distance - b.distance);
    setDeliveries(mockDeliveries);
  }, [driverLocation]);

  useEffect(() => {
    // Generate mock deliveries
    generateMockDeliveries();
    
    // Refresh every 30 seconds
    const interval = setInterval(generateMockDeliveries, 30000);
    return () => clearInterval(interval);
  }, [generateMockDeliveries]);

  const handleSelectDelivery = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      if (selectedIds.length >= Math.min(5, maxPackages)) {
        alert(`You can only select up to ${Math.min(5, maxPackages)} deliveries at a time`);
        return;
      }
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handlePickup = () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one delivery');
      return;
    }

    const selectedDeliveries = deliveries.filter(d => selectedIds.includes(d.id));
    onPickup(selectedDeliveries);
  };

  const filteredDeliveries = deliveries.filter(delivery => {
    if (filter === 'nearby') return delivery.distance < 3;
    if (filter === 'high-pay') return delivery.payment >= 2.80;
    return true;
  });

  const totalEarnings = selectedIds.reduce((sum, id) => {
    const delivery = deliveries.find(d => d.id === id);
    return sum + (delivery ? delivery.payment : 0);
  }, 0);

  return (
    <div className="available-deliveries">
      <div className="section-header">
        <h2>Available Deliveries</h2>
        <button className="btn-refresh" onClick={generateMockDeliveries}>
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="filter-section">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({deliveries.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'nearby' ? 'active' : ''}`}
          onClick={() => setFilter('nearby')}
        >
          Nearby ({deliveries.filter(d => d.distance < 3).length})
        </button>
        <button 
          className={`filter-btn ${filter === 'high-pay' ? 'active' : ''}`}
          onClick={() => setFilter('high-pay')}
        >
          High Pay ({deliveries.filter(d => d.payment >= 2.80).length})
        </button>
      </div>

      {/* Selection Summary */}
      {selectedIds.length > 0 && (
        <div className="selection-summary">
          <div className="summary-info">
            <span className="selected-count">{selectedIds.length} selected</span>
            <span className="selected-earnings">Total: €{totalEarnings.toFixed(2)}</span>
          </div>
          <button className="btn-pickup" onClick={handlePickup}>
            Accept & Pickup
          </button>
        </div>
      )}

      {/* Info Banner */}
      <div className="info-banner">
        <span className="info-icon">💡</span>
        <span>You can select up to {Math.min(5, maxPackages)} packages for efficient group delivery</span>
      </div>

      {/* Deliveries List */}
      <div className="deliveries-list">
        {filteredDeliveries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No deliveries available</h3>
            <p>Check back soon for new delivery opportunities</p>
          </div>
        ) : (
          filteredDeliveries.map(delivery => (
            <div 
              key={delivery.id}
              className={`delivery-card ${selectedIds.includes(delivery.id) ? 'selected' : ''} ${delivery.priority === 'high' ? 'priority-high' : ''}`}
              onClick={() => handleSelectDelivery(delivery.id)}
            >
              <div className="delivery-header">
                <div className="order-info">
                  <span className="order-id">{delivery.orderId}</span>
                  <span className="package-code">{delivery.packageCode}</span>
                </div>
                <div className="delivery-header-right">
                  {delivery.priority === 'high' && (
                    <div className="priority-badge">⚡ Priority</div>
                  )}
                  <div className="payment-badge">€{delivery.payment.toFixed(2)}</div>
                </div>
              </div>

              <div className="delivery-body">
                <div className="location-info">
                  <div className="location-row">
                    <span className="location-icon">🏪</span>
                    <span className="location-text">{delivery.warehouse}</span>
                  </div>
                  <div className="location-arrow">↓</div>
                  <div className="location-row">
                    <span className="location-icon">📍</span>
                    <span className="location-text">{delivery.deliveryAddress}</span>
                  </div>
                </div>

                <div className="delivery-stats">
                  <div className="stat-item">
                    <span className="stat-icon">🛣️</span>
                    <span className="stat-value">{delivery.distance} km</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon">⏱️</span>
                    <span className="stat-value">{delivery.estimatedTime} min</span>
                  </div>
                </div>
              </div>

              <div className="delivery-footer">
                <span className="time-posted">
                  Posted {Math.floor((Date.now() - new Date(delivery.createdAt)) / 60000)} min ago
                </span>
                {selectedIds.includes(delivery.id) && (
                  <span className="selected-indicator">✓ Selected</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AvailableDeliveries;
